import { useQuery } from '@tanstack/react-query'
import {
  MultipleChoiceGroup,
  TemplatesApi,
} from '@ulysses-inc/harami_api_client'
import { baseClient } from 'src/state/middleware/saga/baseClient'
import { handleHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { ExhaustiveError } from 'src/util/error'
import { TemplateEditMode } from '../../context/TemplateEditMode/TemplateEditMode'
import { templateSpecificMultipleChoicesQueryKeys } from '../api/queryKeys'

export const getTemplateSpecificQueryOptions = (
  templateEditMode: TemplateEditMode,
) => {
  switch (templateEditMode.mode) {
    case 'new':
    case 'newFromPreparedTemplate':
      return {
        queryKey: templateSpecificMultipleChoicesQueryKeys.bySession(
          templateEditMode.sessionId,
        ),
        staleTime: Infinity,
        // 質問コンポーネントがアンマウントされるとキャッシュが削除されてしまうため、gcTimeを長めに設定
        gcTime: 7 * 24 * 60 * 60 * 1000, // 7日
      } as const
    case 'edit':
      return {
        queryKey: templateSpecificMultipleChoicesQueryKeys.byTemplateId(
          templateEditMode.templateId,
        ),
      } as const
    default: {
      throw new ExhaustiveError(templateEditMode)
    }
  }
}

/**
 * ひな形限定の選択肢を管理するためのhook (TanStack Query)
 *
 * - ひな形新規作成時: sessionId ごとにキャッシュを分離
 * - ひな形編集時: templateId ごとにキャッシュを分離
 */
export const useQueryTemplateSpecificMultipleChoices = (
  templateEditMode: TemplateEditMode,
) => {
  const { queryKey, staleTime, gcTime } =
    getTemplateSpecificQueryOptions(templateEditMode)
  return useQuery({
    queryKey,
    queryFn: async (): Promise<MultipleChoiceGroup[]> => {
      switch (templateEditMode.mode) {
        case 'new':
        case 'newFromPreparedTemplate':
          // 新規作成時: 空配列で初期化（APIは呼ばない）
          return []
        case 'edit':
          // 編集時: サーバーから取得
          return await baseClient
            .getApi(TemplatesApi)
            .getTemplateResponseMultipleChoices({
              templateId: templateEditMode.templateId,
            })
            .catch(handleHTTPError)
        default: {
          throw new ExhaustiveError(templateEditMode)
        }
      }
    },
    staleTime,
    gcTime,
  })
}
